import { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, PlusCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Avatar from '@/components/Avatar';
import { useToast } from '@/components/Toast';
import { useGroupMembers } from '@/hooks/useGroupMembers';
import type { SignupSlot } from '@flock-types/signupSheets.types';

interface Props {
  groupId: string;
  slot: SignupSlot;
  assignMember: (slotId: string, userId: string) => Promise<void>;
  onClose: () => void;
}

/** Leader assigns a group member to a slot (port of the Expo assign modal). */
export default function AssignMemberModal({ groupId, slot, assignMember, onClose }: Props) {
  const { showToast } = useToast();
  const { members, loading } = useGroupMembers(groupId);
  const [search, setSearch] = useState('');
  const [assigningUserId, setAssigningUserId] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const signedUpIds = new Set((slot.entries ?? []).map((e) => e.user_id));
    return members
      .filter((m) => !q || m.userName.toLowerCase().includes(q))
      .map((m) => ({ ...m, alreadySignedUp: signedUpIds.has(m.userId) }));
  }, [members, search, slot.entries]);

  const handleAssign = async (userId: string) => {
    setAssigningUserId(userId);
    try {
      await assignMember(slot.id, userId);
      onClose();
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Failed to assign member.', 'error');
    } finally {
      setAssigningUserId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex max-h-[70vh] w-full max-w-md flex-col rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-flock-navy-900">Assign Member</h2>
          <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="relative mb-3">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-300" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members..."
            className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 py-2 pl-9 pr-3 text-sm text-flock-navy-900 outline-none placeholder:text-flock-navy-300 focus:border-flock-navy-400"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="py-8 text-center text-sm text-flock-navy-400">Loading members…</p>
          ) : filteredMembers.length === 0 ? (
            <p className="py-6 text-center text-sm text-flock-navy-400">
              {search ? `No members match "${search}"` : 'No members found.'}
            </p>
          ) : (
            filteredMembers.map((member) => (
              <button
                key={member.userId}
                onClick={() => !member.alreadySignedUp && handleAssign(member.userId)}
                disabled={member.alreadySignedUp || assigningUserId !== null}
                className="flex min-h-touch w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition enabled:hover:bg-flock-navy-50 disabled:opacity-40"
              >
                <Avatar
                  src={member.profileImageUrl}
                  name={member.userName}
                  sizeClass="h-8 w-8"
                  textClass="text-xs"
                  bgClass="bg-flock-navy-500"
                />
                <span className="min-w-0 flex-1 truncate text-base text-flock-navy-800">
                  {member.userName}
                </span>
                {member.alreadySignedUp ? (
                  <span className="shrink-0 text-xs text-flock-navy-400">Already signed up</span>
                ) : assigningUserId === member.userId ? (
                  <span className="shrink-0 text-xs text-flock-navy-400">Assigning…</span>
                ) : (
                  <PlusCircleIcon className="h-6 w-6 shrink-0 text-flock-navy-600" />
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
